import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightning, ArrowRight, ArrowLeft, Check, Fingerprint } from '@phosphor-icons/react';
import axios from 'axios';
import VoiceTextarea from './VoiceTextarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: 'rabbit_hole_1',
    category: 'RABBIT HOLE',
    categoryTag: 'OBSESSION',
    question: 'Welches Thema hat dich zuletzt so gefesselt, dass du die Zeit vergessen hast?',
    placeholder: 'z.B. Quantenphysik, Kochen, alte Karten...',
    hint: 'Denk an die letzten 3 Monate. Was hat dich um 3 Uhr nachts wach gehalten?'
  },
  {
    id: 'rabbit_hole_2',
    category: 'RABBIT HOLE',
    categoryTag: 'DEEP DIVE',
    question: 'Wenn du 48 Stunden am Stueck recherchieren koenntest — welche 3 Themen?',
    placeholder: 'Thema 1, Thema 2, Thema 3',
    hint: 'Keine praktischen Einschraenkungen. Reine Neugier.'
  },
  {
    id: 'rabbit_hole_3',
    category: 'RABBIT HOLE',
    categoryTag: 'LOOP',
    question: 'Welches YouTube/Podcast/Wikipedia-Rabbit-Hole zieht dich immer wieder rein?',
    placeholder: 'z.B. True Crime, Architektur, Philosophie...',
    hint: 'Das Thema, bei dem du immer "noch ein Video" klickst.'
  },
  {
    id: 'childhood_1',
    category: 'KINDHEITS-ENERGIE',
    categoryTag: 'UNKONDITIONIERT',
    question: 'Was hast du als Kind gemacht, wenn niemand zugeschaut hat?',
    placeholder: 'z.B. Geschichten erfunden, Dinge zerlegt, gezeichnet...',
    hint: 'Vor Social Media. Vor Noten. Was war DEIN Ding?'
  },
  {
    id: 'childhood_2',
    category: 'KINDHEITS-ENERGIE',
    categoryTag: 'FLOW',
    question: 'Welches Spiel oder Hobby hat dich als Kind am meisten begeistert?',
    placeholder: 'z.B. LEGO, Baumhaus bauen, Rollenspiele...',
    hint: 'Die Aktivitaet, bei der du das Abendessen vergessen hast.'
  },
  {
    id: 'childhood_3',
    category: 'KINDHEITS-ENERGIE',
    categoryTag: 'VERSCHUETTET',
    question: 'Welches Talent hattest du als Kind, das du nie weiterentwickelt hast?',
    placeholder: 'z.B. Zeichnen, Musik, Geschichten erzaehlen...',
    hint: 'Etwas, das dir leicht fiel — aber irgendwann verschwand.'
  },
  {
    id: 'frustration_1',
    category: 'FRUSTRATIONS-INDIKATOR',
    categoryTag: 'TRIGGER',
    question: 'Was bringt dich bei anderen Menschen sofort auf die Palme?',
    placeholder: 'z.B. Unehrlichkeit, Inkompetenz, Gleichgueltigkeit...',
    hint: 'Was dich nervt = invertierter Wert. Dein Trigger zeigt, was dir WICHTIG ist.'
  },
  {
    id: 'frustration_2',
    category: 'FRUSTRATIONS-INDIKATOR',
    categoryTag: 'MISSION',
    question: 'Welches gesellschaftliche Problem wuerdest du sofort loesen, wenn du koenntest?',
    placeholder: 'z.B. Bildungssystem, Einsamkeit, Klimawandel...',
    hint: 'Nicht was "man" loesen sollte. Was DICH persoenlich wuetend macht.'
  },
  {
    id: 'social_1',
    category: 'SOZIALES ARTEFAKT',
    categoryTag: 'BLINDE STAERKE',
    question: 'Wofuer fragen dich Freunde oder Familie immer wieder um Rat?',
    placeholder: 'z.B. Technik, Beziehungen, Karriere, Organisation...',
    hint: 'Andere sehen Staerken, die du selbst fuer normal haeltst.'
  },
  {
    id: 'social_2',
    category: 'SOZIALES ARTEFAKT',
    categoryTag: 'HIDDEN GEM',
    question: 'Welche Faehigkeit besitzt du, die andere immer ueberrascht?',
    placeholder: 'z.B. Schnelles Lernen, Empathie, Problemloesung...',
    hint: 'Das Ding, bei dem Leute sagen: "Wusste ich gar nicht von dir."'
  },
  {
    id: 'legacy_quest',
    category: 'DER ANTRIEB',
    categoryTag: 'LEGACY QUEST',
    question: 'Wenn du auf die naechsten 10-15 Jahre blickst: Was ist dein Nordstern?',
    type: 'tags',
    tags: [
      { id: 'system_mastery', label: 'System-Meisterschaft', desc: 'Ein bestehendes System perfektionieren' },
      { id: 'radical_creation', label: 'Radikale Neuschoepfung', desc: 'Etwas vollkommen Neues aus dem Boden stampfen' },
      { id: 'absolute_autonomy', label: 'Absolute Autonomie', desc: 'Freiheit durch maximale Effizienz zurueckkaufen' },
      { id: 'impact_legacy', label: 'Impact & Vermaechtnis', desc: 'Etwas hinterlassen, das groesser ist als du' }
    ],
    hint: 'Waehle den Antrieb, der dich am staerksten anzieht. Dieser wird dein Nordstern fuer die Roadmap.'
  }
];

const CATEGORY_COLORS = {
  'RABBIT HOLE': 'var(--accent-gold)',
  'KINDHEITS-ENERGIE': '#FBBF24',
  'FRUSTRATIONS-INDIKATOR': '#EF4444',
  'SOZIALES ARTEFAKT': '#22C55E',
  'DER ANTRIEB': '#A855F7'
};

const HiddenTalentScan = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const currentQuestion = QUESTIONS[currentIndex];
  const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
  const categoryColor = CATEGORY_COLORS[currentQuestion.category] || '#fff';

  const handleAnswer = (value) => {
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleNext = () => {
    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/user/hidden-talent`, {
        session_id: sessionId,
        answers
      });
      navigate(`/instinct-profile/${sessionId}`);
    } catch (err) {
      console.error('Error submitting hidden talent:', err);
      setSubmitting(false);
    }
  };

  const isLastQuestion = currentIndex === QUESTIONS.length - 1;
  const currentAnswer = answers[currentQuestion.id] || '';
  const answeredCount = Object.keys(answers).filter(k => {
    const val = answers[k];
    return typeof val === 'string' && val.trim();
  }).length;

  return (
    <div className="page-shell-bg">
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-2xl page-shell-card"
      >
        {/* Header */}
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: categoryColor }}>
              <Lightning size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Block 0 — Hidden Talent Scan</h2>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>The Raw Truth</p>
            </div>
          </div>
          <span className="text-xs px-3 py-1 font-semibold" style={{ border: '1px solid var(--border-default)', borderRadius: '9999px', color: 'var(--text-secondary)' }}>
            {currentIndex + 1} / {QUESTIONS.length}
          </span>
        </div>

        {/* Progress */}
        <div className="h-1" style={{ background: 'var(--border-default)' }}>
          <motion.div className="h-full" style={{ background: categoryColor, borderRadius: '0 4px 4px 0' }}
            animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>

        {/* Content */}
        <div className="page-shell-body">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.3 }}
            >
              {/* Category Tag */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-bold px-3 py-1 rounded-full"
                  style={{ background: categoryColor, color: '#fff' }}
                  data-testid="question-category">
                  {currentQuestion.categoryTag}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {currentQuestion.category}
                </span>
              </div>

              {/* Question */}
              <h2 className="text-xl md:text-2xl font-bold mb-3 leading-tight" data-testid="question-text">
                {currentQuestion.question}
              </h2>

              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                {currentQuestion.hint}
              </p>

              {/* Input */}
              {currentQuestion.type === 'tags' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tag-input">
                  {currentQuestion.tags.map((tag) => {
                    const isSelected = currentAnswer === tag.id;
                    return (
                      <motion.button key={tag.id} whileTap={{ scale: 0.97 }}
                        onClick={() => handleAnswer(tag.id)}
                        className={`page-shell-tag text-left ${isSelected ? 'page-shell-tag-selected' : ''}`}
                        style={isSelected ? { background: categoryColor, borderColor: categoryColor, color: '#fff' } : {}}
                        data-testid={`tag-${tag.id}`}>
                        <span className="font-bold text-sm block mb-1">{tag.label}</span>
                        <span className="text-xs" style={{ opacity: 0.7 }}>{tag.desc}</span>
                      </motion.button>
                    );
                  })}
                </div>
              ) : (
                <VoiceTextarea
                  value={currentAnswer}
                  onChange={(e) => handleAnswer(e.target.value)}
                  placeholder={currentQuestion.placeholder}
                  rows={3}
                  testId="question-input"
                  autoFocus
                  style={{ borderColor: currentAnswer.trim() ? categoryColor : 'var(--border-default)' }}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          {/* Progress dots */}
          {(() => {
            const dotEls = QUESTIONS.map((q, i) => (
              <div key={q.id} className="w-2 h-2 rounded-full transition-all cursor-pointer flex-shrink-0"
                onClick={() => setCurrentIndex(i)}
                style={{
                  background: (typeof answers[q.id] === 'string' && answers[q.id]?.trim())
                    ? CATEGORY_COLORS[q.category]
                    : i === currentIndex ? 'var(--text-primary)' : 'var(--border-default)',
                }}
                data-testid={`dot-${i}`} />
            ));
            return (
              <div className="mt-8">
                {/* Mobile: dots on their own centered row above the buttons */}
                <div className="flex flex-wrap justify-center gap-1.5 mb-4 sm:hidden">
                  {dotEls}
                </div>

                <div className="flex items-center justify-between gap-3">
                  <button onClick={handlePrev} disabled={currentIndex === 0}
                    className="page-shell-btn page-shell-btn-outline"
                    style={{ width: 'auto', padding: '0.625rem 1.25rem', opacity: currentIndex === 0 ? 0.3 : 1 }}
                    data-testid="prev-btn">
                    <ArrowLeft size={16} /> Zurueck
                  </button>

                  {/* Desktop: dots in the middle */}
                  <div className="hidden sm:flex gap-1.5">
                    {dotEls}
                  </div>

                  {isLastQuestion ? (
                    <button onClick={handleSubmit} disabled={submitting || answeredCount < 5}
                      className="page-shell-btn"
                      style={{ width: 'auto', padding: '0.625rem 1.25rem' }}
                      data-testid="submit-btn">
                      {submitting ? 'Wird gespeichert...' : <><Check size={16} weight="bold" /> Abschliessen ({answeredCount}/11)</>}
                    </button>
                  ) : (
                    <button onClick={handleNext}
                      className="page-shell-btn"
                      style={{ width: 'auto', padding: '0.625rem 1.25rem' }}
                      data-testid="next-btn">
                      Weiter <ArrowRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          <p className="text-center text-xs mt-6" style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>
            Mindestens 5 von 11 Fragen beantworten. Je mehr, desto praeziser dein Profil.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default HiddenTalentScan;
